/**
 * GarminConnectionCard — connect, inspect and disconnect a Garmin account.
 *
 * The disconnect is destructive in a way the user cannot undo and we cannot
 * reverse: Garmin Starter Access has no historical access, so a staged activity
 * that gets deleted here can never be fetched again. The confirmation says so
 * in plain words rather than asking "are you sure?".
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Divider, Typography, useTheme,
} from '@mui/material';
import WatchIcon from '@mui/icons-material/Watch';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { disconnectGarmin, getGarminAuthUrl, getGarminStatus } from '../../api/garminApi';

const fmtDate = (value) => (value ? new Date(value).toLocaleDateString() : null);

const GarminConnectionCard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getGarminStatus()
      .then(setStatus)
      .catch(() => setError(t('garminStatusFailed', 'Could not load the Garmin connection status')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const handleConnect = async () => {
    setBusy(true);
    try {
      window.location.href = await getGarminAuthUrl();
    } catch {
      setError(t('garminConnectFailed', 'Could not start the Garmin connection'));
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    setConfirmOpen(false);
    try {
      await disconnectGarmin();
      setError('');
      load();
    } catch {
      setError(t('garminDisconnectFailed', 'Could not disconnect your Garmin account'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>;
  }
  if (!status) return null;

  const connected = status.connected;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <WatchIcon sx={{ color: colors.blueAccent[400] }} />
        <Typography variant="h6" color={colors.grey[100]}>
          Garmin Connect™
        </Typography>
        <Chip
          size="small"
          label={connected
            ? t('connected', 'Connected')
            : t('notConnected', 'Not connected')}
          sx={{
            backgroundColor: connected ? colors.greenAccent[700] : colors.grey[700],
            color: colors.grey[100],
          }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!status.available && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('garminNotAvailable', 'This integration is not enabled for your account yet.')}
        </Alert>
      )}

      {connected && !status.activity_export_enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('garminNoActivityPermission',
            'Activity sharing is switched off in your Garmin account, so no new activities will arrive. Turn it back on in your Garmin Connect account settings.')}
        </Alert>
      )}

      {connected ? (
        <Box sx={{ color: colors.grey[300] }}>
          <Typography variant="body2">
            {t('garminConnectedSince', 'Connected since')}: <strong>{fmtDate(status.connected_at) || '—'}</strong>
          </Typography>
          <Typography variant="body2">
            {t('garminLastActivity', 'Last activity received')}: <strong>{fmtDate(status.last_push_at) || t('none', 'none yet')}</strong>
          </Typography>
          <Typography variant="body2">
            {t('garminWaitingToImport', 'Waiting to be imported')}:{' '}
            <strong>{status.importable_activities}</strong>
            {status.staged_activities !== status.importable_activities
              && ` (${status.staged_activities} ${t('received', 'received')})`}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.grey[400], display: 'block', mt: 1.5 }}>
            {t('garminRetentionNote',
              'Activities you do not import are deleted automatically after {{days}} days.',
              { days: status.retention_days })}
          </Typography>

          <Divider sx={{ my: 2, borderColor: colors.grey[700] }} />

          <Button
            variant="outlined"
            color="error"
            startIcon={<LinkOffIcon />}
            disabled={busy}
            onClick={() => setConfirmOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            {t('garminDisconnect', 'Disconnect Garmin Connect™')}
          </Button>
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" sx={{ color: colors.grey[300], mb: 2, maxWidth: 560 }}>
            {t('garminConnectBlurb',
              'Link your Garmin Connect account and your runs and rides will appear here automatically, ready to analyse.')}
          </Typography>
          <Button
            variant="contained"
            disabled={!status.available || !status.configured || busy}
            onClick={handleConnect}
            sx={{ textTransform: 'none', backgroundColor: colors.blueAccent[600] }}
          >
            {t('garminConnectCta', 'Connect Garmin Connect™')}
          </Button>
          <Typography variant="caption" sx={{ color: colors.grey[400], display: 'block', mt: 2, maxWidth: 560 }}>
            {t('garminHistoryNote',
              'Only activities recorded after you connect will appear here. For an older race, upload the file from your computer.')}
          </Typography>
        </Box>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t('garminDisconnectTitle', 'Disconnect Garmin Connect™?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('garminDisconnectBody',
              'Garmin will stop sending new activities, and everything we hold from your Garmin account will be deleted: activities waiting to be imported, the files you imported, and the analyses, comparisons and predictions made from them. Files you uploaded yourself are not affected.')}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: 600 }}>
            {t('garminDisconnectIrreversible',
              'This cannot be undone — Garmin does not allow past activities to be fetched again.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none' }}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button onClick={handleDisconnect} color="error" variant="contained" sx={{ textTransform: 'none' }}>
            {t('garminDisconnectConfirm', 'Disconnect and delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GarminConnectionCard;
